import { PropType } from 'vue';
import moment from 'moment';

import mixins from '@/utils/mixins';
import colorable from './colorable';
import snackbar from './snackbar';
import { IQuoteData, Quote } from '@/models/quote';

import { authProvider } from '@vue-altoleap-libraries/vue-altoleap-accounts-lib';
import { Group } from '@vue-altoleap-libraries/vue-altoleap-accounts-lib/types/model/userData';
import { mapActions, mapGetters } from 'vuex';
import { ErrorManager } from '@/models/error';
import auth from '@/mixins/auth';
import QuoteRFQFormDialog from '@/components/quote-common/QuoteRFQFormDialog.vue';
import { omitDeep } from '@/utils/helpers';
import { delay } from 'lodash';

const baseMixins = mixins(colorable, snackbar, auth);
export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    quoteRFQFormDialog: InstanceType<typeof QuoteRFQFormDialog>;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'QuoteState',

    components: {
      QuoteRFQFormDialog
    },

    props: {
      quote: Object as PropType<Quote>
    },

    data: () => ({
      errorMessage: '',
      detailErrorMessage: ''
    }),

    computed: {
      ...mapGetters({
        getQuoteById: 'quote/getQuoteById'
      }),

      getColor(): string {
        return '';
      },

      getIcon(): string {
        return '';
      },

      getString(): string {
        return '';
      },

      getActionText(): string {
        return '';
      },

      authProvider(): any {
        return authProvider(this.$store);
      },

      isUserOrderManagementOrCustomerSupport(): boolean {
        const userGroups: Group[] =
          this.authProvider.getCurrentUser.groups ?? [];

        const eligible = userGroups.find(
          ({ name }) =>
            name === 'Order Management' || name === 'Customer Support'
        );

        return eligible || this.authProvider.isUserAdmin;
      },

      canBuildPackage(): boolean {
        return (
          this.userHasPermissions([
            'quotes.add_quotepackage',
            'quotes.change_quotepackage',
            'quotes.download_quotepackage'
          ]) && !this.quote.is_budgetary
        );
      }
    },

    methods: {
      ...mapActions({
        exportQuoteRevisionItems: 'quote/exportQuoteItems',
        exportQuoteRevisionItemsMargins: 'quote/exportQuoteItemsMargins',
        createQuoteRevision: 'quote/createQuoteRevision'
      }),

      submit() {
        throw new Error('Invalid State');
      },

      undoSubmit() {
        throw new Error('Invalid State');
      },

      startReview() {
        throw new Error('Invalid State');
      },

      approve() {
        throw new Error('Invalid State');
      },

      prepareForCustomer() {
        throw new Error('Invalid State');
      },

      sendToCustomer() {
        throw new Error('Invalid State');
      },

      newRevision() {
        throw new Error('Invalid State');
      },

      rejected() {
        throw new Error('Invalid State');
      },

      accepted() {
        throw new Error('Invalid State');
      },

      processOrder() {
        throw new Error('Invalid State');
      },

      reviewDiscounts() {
        throw new Error('Invalid State');
      },

      async openProposalDialog() {
        // using `any` as typescript files cannot import vue files directly
        const dialog = this.$refs.quotePdfLanguageDialog as any;
        dialog.open(this.quote);
      },

      async buildQuoteDocument() {
        // using `any` as typescript files cannot import vue files directly
        const dialog = this.$refs.quoteDocumentBuilderDialog as any;
        dialog.open(this.quote);
      },

      async openProformaInvoiceDialog() {
        // using `any` as typescript files cannot import vue files directly

        const dialog = this.$refs.quoteProformaFormDialog as any;
        dialog.open(this.quote);
      },

      async openRFQFormDialog() {
        this.$refs.quoteRFQFormDialog.open();
      },

      clearAllErrors(): void {
        this.errorMessage = '';
        this.detailErrorMessage = '';
      },

      exportQuoteItems() {
        this.clearAllErrors();
        this.exportQuoteRevisionItems({
          quoteId: this.quote.number,
          revisionId: this.quote.revision
        })

          .then((data: Blob) => {
            this.createQuoteItemXlsx(data, this.quote);
          })
          .catch(error => {
            // under no circumstance will we have access to the response.data as it is a blob file
            this.errorMessage = ErrorManager.extractApiError(error);
          });
      },

      exportQuoteItemsMargins() {
        this.clearAllErrors();
        this.exportQuoteRevisionItemsMargins({
          quoteId: this.quote.number,
          revisionId: this.quote.revision
        })

          .then((data: Blob) => {
            this.createQuoteItemXlsx(data, this.quote);
          })
          .catch(error => {
            // under no circumstance will we have access to the response.data as it is a blob file
            this.errorMessage = ErrorManager.extractApiError(error);
          });
      },

      createQuoteItemXlsx(data: Blob, quote: Quote) {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        const linkName = `quote-${quote.number}-${quote.revision}-${moment(
          new Date()
        ).format('YYYY-MM-DD-HH-mm-ss')}.xlsx`;
        link.href = url;

        link.setAttribute('download', linkName); //or any other extension
        document.body.appendChild(link);
        link.click();
      },

      reloadQuoteCard(quote: Quote) {
        this.$router.replace({
          name: 'quote-revision-detail',
          params: {
            quoteId: String(quote.number),
            quoteRevision: String(quote.revision!)
          }
        });
        window.location.reload();
      },

      createNewQuoteRevision(quoteData: IQuoteData) {
        //TODO: LF-2021-09-02 - Temporarily added agent to omit list
        // This is to prevent a crash where agent is being sent as
        // string and not as ID as expected. To be removed once fixed

        const idOmittedQuote = omitDeep(this.quote, [
          'id',
          'agent',
          'inside_sales_owner'
        ]);

        //add new rfq date
        idOmittedQuote.rfq_date = quoteData.rfq_date!;
        idOmittedQuote.rfq_number = quoteData.rfq_number!;

        return this.createQuoteRevision(idOmittedQuote).then(() => {
          const quote = this.getQuoteById(Number(this.$route.params.quoteId));

          this.createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: 'Created revision successfully',
            btnText: 'Ok',
            timeout: 2000
          }).then(() => {
            delay(this.reloadQuoteCard, 1000, quote);
          });
        });
      }
    }
  });
